import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { Box, Button, Snackbar, styled, Typography } from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import StarIcon from "@mui/icons-material/Star";
import makeFullUrl from "../../utils/cloudinary";

// Hidden input to upload images
const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const UploadImageForm = forwardRef(
  ({ onDataChange, imageInfo, initialData }, ref) => {
    // Handle image change functionality STARTS
    const [selectedImages, setSelectedImages] = useState([]);

    useEffect(() => {
      if (!initialData) return;

      // Create stable initial images array
      const initialImages = (initialData.images || []).map((img) => ({
        ...img,
        url: img.filePath
          ? // ? `${process.env.REACT_APP_SERVER_URL}/${img.filePath}`
            makeFullUrl(img.filePath, img.fileType)
          : URL.createObjectURL(img.file),
        isMain: false,
      }));

      // Handle main image
      const mainImage = initialData.mainImage
        ? {
            ...initialData.mainImage,
            url: initialData.mainImage.filePath
              ? // ? `${process.env.REACT_APP_SERVER_URL}/${initialData.mainImage.filePath}`
                makeFullUrl(
                  initialData.mainImage.filePath,
                  initialData.mainImage.fileType
                )
              : URL.createObjectURL(initialData.mainImage.file),
            isMain: true,
          }
        : null;

      setSelectedImages(
        mainImage ? [mainImage, ...initialImages] : initialImages
      );
    }, []);

    // Prevent memory leaks by revoking object URLs when images are removed or the component unmounts
    useEffect(() => {
      return () => {
        selectedImages.forEach((image) => {
          if (image.url.startsWith("blob:")) {
            URL.revokeObjectURL(image.url);
          }
        });
      };
    }, []);

    const [errors, setErrors] = useState({
      image: false,
      imageType: false,
      mainImage: false,
    });

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarImageSizeOpen, setSnackbarImageSizeOpen] = useState(false);
    const [fileSizeName, setFileSizeName] = useState([]);

    const uploadButtonRef = useRef(null);
    const mainImageErrorRef = useRef(null); // Ref for main image error message

    const validImageTypes = [
      "image/jpeg",
      "image/png",
      // "image/gif",
      "image/bmp",
      "image/webp",
    ];
    const MAX_IMAGES = 6;
    const MAX_FILE_SIZE_MB = 5 * 1024 * 1024; // 5MB

    // Handle image selection
    const handleImageChange = useCallback(
      async (event) => {
        const files = Array.from(event.target.files);

        // 1. Check if more than 6 images are selected
        if (selectedImages.length + files.length > MAX_IMAGES) {
          // Show snackbar if trying to upload more than 6 images
          setSnackbarOpen(true);
          return;
        }

        // Filter valid images
        const validFiles = files.filter((file) =>
          validImageTypes.includes(file.type)
        );

        // If there are invalid files, alert the user and clear the input
        if (validFiles.length !== files.length) {
          setErrors((prev) => ({ ...prev, imageType: true }));
          uploadButtonRef.current.focus();
          return; // Prevent submission if no images are selected
        } else {
          setErrors((prev) => ({ ...prev, imageType: false }));
        }

        // 3. Check image size (5 MB limit)
        const oversizedFiles = validFiles.filter(
          (file) => file.size >= MAX_FILE_SIZE_MB
        );

        if (oversizedFiles.length > 0) {
          setFileSizeName(oversizedFiles.map((file) => file.name));
          setSnackbarImageSizeOpen(true);
          return; // Prevent submission if image size is greater than 5 MB
        }

        // Convert images to WebP
        const webpFiles = await Promise.all(
          validFiles.map((file) => convertToWebP(file))
        );

        // Add new images to the state
        const newImages = webpFiles.map((file) => ({
          url: URL.createObjectURL(file),
          file,
          isMain: false, // Explicitly set initial state
        }));

        // Update state
        setSelectedImages((prevImages) => [...prevImages, ...newImages]);

        setErrors((prev) => ({ ...prev, image: false }));
      },
      [selectedImages]
    );

    // Handle image removal
    const handleRemoveImage = useCallback((index) => {
      setSelectedImages((prevImages) => {
        const removedImage = prevImages[index];
        const updatedImages = prevImages.filter((_, i) => i !== index);

        if (removedImage.isMain) {
          setErrors((prev) => ({ ...prev, mainImage: true }));
        }

        return updatedImages;
      });
    }, []); // Dependency ensures latest mainImage value

    // Set main image
    // const handleSetMainImage = useCallback(
    //   (index) => {
    //     const newMainImage = selectedImages[index].file;
    //     setMainImage(newMainImage);
    //     setErrors((prev) => ({ ...prev, mainImage: false }));

    //     // onDataChange({
    //     //   images: selectedImages.filter((img) => img.file !== newMainImage),
    //     //   mainImage: newMainImage,
    //     // });
    //   },
    //   // [selectedImages, onDataChange]
    //   [selectedImages]
    // );

    const handleSetMainImage = useCallback((index) => {
      setSelectedImages((prev) =>
        prev.map((img, i) => ({ ...img, isMain: i === index }))
      );
      setErrors((prev) => ({ ...prev, mainImage: false }));
    }, []);

    // Close snackbars
    const handleSnackbarClose = useCallback(() => {
      setSnackbarOpen(false);
      setSnackbarImageSizeOpen(false);
    }, []);
    // Handle image change functionality ENDS

    // Convert images to webp format
    // async function convertToWebP(file) {
    const convertToWebP = async (file) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);

          canvas.toBlob((blob) => {
            // Create a new File object with a specific name
            const webpFile = new File(
              [blob],
              file.name.replace(/\.\w+$/, ".webp"),
              { type: "image/webp" }
            );
            resolve(webpFile);
          }, "image/webp");
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    };

    // Validate all form fields on submit
    const validateUserInput = useCallback(() => {
      // 1. Check if any image selected to upload & main image is set
      if (selectedImages.length === 0) {
        setErrors((prev) => ({ ...prev, image: true }));
        uploadButtonRef.current.focus();
        return false; // Prevent submission if no images are selected
      }

      // 2. Check if main image is selected using the actual data
      const hasMainImage = selectedImages.some((img) => img.isMain);

      if (!hasMainImage) {
        setErrors((prev) => ({ ...prev, mainImage: true }));
        // uploadButtonRef.current.focus();
        mainImageErrorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return false; // Prevent submission if main image is not selected
      }
      return true;
    }, [selectedImages]);

    useEffect(() => {
      const mainImageData = selectedImages.find((img) => img.isMain);
      const otherImages = selectedImages.filter((img) => !img.isMain);

      const newData = {
        images: otherImages,
        mainImage: mainImageData || null,
      };

      if (JSON.stringify(newData) !== JSON.stringify(prevDataRef.current)) {
        onDataChange(newData);
        prevDataRef.current = newData;
      }
    }, [selectedImages, onDataChange]);

    // Expose the validate method to the parent via ref
    useImperativeHandle(ref, () => ({
      validateUserInput,
    }));

    // Initialize a ref to store the previous data
    const prevDataRef = useRef({
      images: [],
      mainImage: null,
    });

    // Scroll to main image error when needed
    useEffect(() => {
      if (errors.mainImage && mainImageErrorRef.current) {
        mainImageErrorRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, [errors.mainImage]);

    return (
      <>
        {/* 1. Pictures Field */}
        <Button
          component="label"
          role={undefined}
          variant="outlined"
          tabIndex={-1}
          startIcon={<PhotoCameraIcon />}
          color="inherit"
          sx={{ width: { xs: "75%", sm: 400 } }}
          size="large"
          ref={uploadButtonRef}
        >
          Add Pictures
          <VisuallyHiddenInput
            type="file"
            accept="image/*"
            name="selectedImages"
            multiple
            onChange={handleImageChange}
          />
        </Button>
        <Typography variant="caption" color="textSecondary" sx={{ mt: -3 }}>
          {imageInfo}
        </Typography>

        {/* Error Messages */}
        {errors.image && (
          <Typography color="error">
            Please upload at least one image.
          </Typography>
        )}
        {errors.imageType && (
          <Typography color="error">
            Please upload only image files (jpg, jpeg, png, bmp, webp).
          </Typography>
        )}

        {/* Image Preview */}
        {selectedImages.length > 0 && (
          <Box
            sx={{
              pb: 3,
              // width: 400,
              width: { xs: "75%" },
              border: "1px dashed black",
            }}
          >
            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="center"
              gap={4}
              mt={2}
            >
              {selectedImages.map((image, index) => (
                <Box
                  key={index}
                  position="relative"
                  sx={{ width: 200, height: 200 }}
                >
                  <img
                    src={image.url}
                    alt={`Selected ${index}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: 8,
                      border: "1px solid rgba(0, 0, 0, 0.23)",
                      // mainImageIndex === index ? "2px solid #007bff" : "none",
                    }}
                  />

                  {/* Set as Main Button */}
                  {!image.isMain && (
                    <Button
                      onClick={() => handleSetMainImage(index)}
                      size="small"
                      variant="contained"
                      color="inherit"
                      sx={{
                        position: "absolute",
                        bottom: -10,
                        left: "50%",
                        transform: "translateX(-50%)",
                        fontSize: 10,
                        padding: "2px 6px",
                      }}
                    >
                      Set Main
                    </Button>
                  )}

                  {/* Main Image Indicator */}
                  {image.isMain && (
                    <StarIcon
                      color="inherit"
                      sx={{
                        position: "absolute",
                        top: -10,
                        right: -10,
                        fontSize: 24,
                      }}
                    />
                  )}

                  {/* Remove Image Button */}
                  <Button
                    onClick={() => handleRemoveImage(index)}
                    size="small"
                    variant="contained"
                    color="inherit"
                    sx={{
                      position: "absolute",
                      top: -13,
                      left: -13,
                      minWidth: 24,
                      minHeight: 24,
                      borderRadius: "50%",
                    }}
                  >
                    x
                  </Button>
                </Box>
              ))}
            </Box>
            {errors.mainImage && (
              <Typography
                color="error"
                sx={{ textAlign: "center", mt: 3 }}
                ref={mainImageErrorRef} // Ref for scrolling to the error
              >
                Please set main image.
              </Typography>
            )}
          </Box>
        )}
        {/* Snackbar for image count feedback */}
        <Snackbar
          open={snackbarOpen}
          onClose={handleSnackbarClose}
          message="You can upload a maximum of 6 images."
          autoHideDuration={4000}
        />
        {/* Snackbar for image size feedback */}
        <Snackbar
          open={snackbarImageSizeOpen}
          onClose={handleSnackbarClose}
          message={`[${fileSizeName.filter(Boolean).join(", ")}] exceeds 5 MB.`}
          // `Image ${image.file.name} exceeds size limit.`
          autoHideDuration={5000}
        />
      </>
    );
  }
);

export default UploadImageForm;
